import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import Modal from '../../components/Modal';
import { includesNormalized } from '../../utils/searchUtils';
import './Enrollments.css';

const Enrollments = () => {
  const { t } = useTranslation();
  const { data, enrollStudent, bulkEnrollStudents, unenrollStudent, updateEnrollment, getCourseById, refresh } = useData();
  const { selectedLocation } = useOutletContext();
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedStudy, setSelectedStudy] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedAssignmentStatus, setSelectedAssignmentStatus] = useState('');
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showBulkEnrollModal, setShowBulkEnrollModal] = useState(false);
  const [enrollModalStudy, setEnrollModalStudy] = useState('');
  const [enrollModalYear, setEnrollModalYear] = useState('');
  const [enrollStudentSearch, setEnrollStudentSearch] = useState('');
  const [bulkYear, setBulkYear] = useState('');
  const [bulkCohort, setBulkCohort] = useState('');
  const [bulkCourse, setBulkCourse] = useState('');
  const [bulkStudentSearch, setBulkStudentSearch] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [editingEnrollment, setEditingEnrollment] = useState(null);
  const [editingStatus, setEditingStatus] = useState('active');
  const [editingStudyYearMode, setEditingStudyYearMode] = useState('auto');
  const [editingCurrentStudyYear, setEditingCurrentStudyYear] = useState('1');
  const [searchTerm, setSearchTerm] = useState('');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [notificationSettings, setNotificationSettings] = useState({
    sendEmail: true,
    sendSms: false
  });
  const [bulkRefreshTrigger, setBulkRefreshTrigger] = useState(0);

  useEffect(() => {
    if (bulkRefreshTrigger > 0) {
      refresh();
    }
  }, [bulkRefreshTrigger, refresh]);

  const periodOptions = [
    { value: 'all', label: 'Alles' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Maand' },
    { value: 'year', label: 'Jaar' }
  ];

  const studies = useMemo(() => data?.studies || [], [data?.studies]);
  const courses = useMemo(() => data?.courses || [], [data?.courses]);

  const toId = (value) => String(value?.id || value?._id || value || '');

  const getCourseStudyYearId = useCallback((course) => {
    return toId(course?.studyYear?.id || course?.studyYear?._id || course?.studyYear || course?.studyYearId);
  }, []);

  const studyYearLookup = useMemo(() => {
    const lookup = new Map();
    studies.forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        const yearId = toId(year.id || year._id);
        if (!yearId) return;
        lookup.set(yearId, { study, year });
      });
    });
    return lookup;
  }, [studies]);

  // Maps moduleId → { study, year } so courses without a direct studyYear can resolve their year via their module.
  const moduleToYearLookup = useMemo(() => {
    const lookup = new Map();
    studies.forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        const yearId = toId(year.id || year._id);
        if (!yearId) return;
        (year.modules || []).forEach((mod) => {
          const moduleId = toId(mod.id || mod._id);
          if (moduleId) lookup.set(moduleId, { study, year });
        });
      });
    });
    return lookup;
  }, [studies]);

  const getCohortDisplayName = (cohort) => {
    const name = String(cohort?.name || '').trim();
    const code = String(cohort?.code || '').trim();
    if (!name) return code;
    if (!code) return name;
    if (name.toLowerCase().includes(code.toLowerCase())) return name;
    return `${name} (${code})`;
  };

  const getStudyOptionLabel = (study) => {
    const cohortNames = (study.cohorts || []).map(getCohortDisplayName).filter(Boolean);
    return cohortNames.length > 0 ? `${study.name} ${cohortNames.join(', ')}` : study.name;
  };

  const courseMetadata = useMemo(() => {
    const entries = [];

    courses.forEach((course) => {
      const directYearId = getCourseStudyYearId(course);
      let linkedYear = studyYearLookup.get(directYearId);
      // Fallback: resolve year via the course's module when direct studyYear link is absent.
      if (!linkedYear) {
        const mids = [
          toId(course?.module?.id || course?.module?._id || course?.module),
          ...(course?.modules || []).map((m) => toId(m?.id || m?._id || m))
        ].filter(Boolean);
        for (const mid of mids) {
          const entry = moduleToYearLookup.get(mid);
          if (entry) { linkedYear = entry; break; }
        }
      }
      if (!linkedYear) return;

      const { study, year } = linkedYear;
      const yearId = toId(year.id || year._id);
      const linkedModules = Array.isArray(course.modules) && course.modules.length > 0
        ? course.modules
        : (course.module ? [course.module] : []);

      if (linkedModules.length === 0) {
        entries.push({
          id: String(course.id || course._id),
          studyId: String(study.id || study._id),
          studyName: study.name,
          studyLocation: study.location,
          yearId,
          yearLabel: year.name || `Studiejaar ${year.yearNumber || year.year}`,
          yearNumber: year.yearNumber || year.year,
          moduleId: `course-${String(course.id || course._id)}-default`,
          moduleName: 'Module nog niet ingesteld',
          courseName: course.name,
          courseCode: course.code,
          courseLocation: course.location
        });
        return;
      }

      linkedModules.forEach((moduleDoc) => {
        const moduleId = String(moduleDoc?.id || moduleDoc?._id || moduleDoc || '');
        entries.push({
          id: String(course.id || course._id),
          studyId: String(study.id || study._id),
          studyName: study.name,
          studyLocation: study.location,
          yearId,
          yearLabel: year.name || `Studiejaar ${year.yearNumber || year.year}`,
          yearNumber: year.yearNumber || year.year,
          moduleId,
          moduleName: moduleDoc?.name || moduleDoc?.code || 'Module',
          courseName: course.name,
          courseCode: course.code,
          courseLocation: course.location
        });
      });
    });

    return entries;
  }, [courses, getCourseStudyYearId, studyYearLookup, moduleToYearLookup]);

  const courseMetadataById = useMemo(() => {
    const mapping = new Map();
    const ordered = [...courseMetadata].sort((a, b) => Number(a.yearNumber || 0) - Number(b.yearNumber || 0));
    ordered.forEach((entry) => {
      if (!mapping.has(entry.id)) {
        mapping.set(entry.id, entry);
      }
    });
    return mapping;
  }, [courseMetadata]);

  const studiesWithAssignments = useMemo(() => {
    const studyIds = new Set(courseMetadata.map((entry) => String(entry.studyId)));
    return studies.filter((study) => studyIds.has(String(study.id || study._id)));
  }, [courseMetadata, studies]);

  const allCourses = useMemo(() => {
    const deduped = new Map();

    courseMetadata.forEach((entry) => {
      if (deduped.has(entry.id)) {
        return;
      }

      const course = getCourseById(entry.id);
      deduped.set(entry.id, {
        ...(course || {}),
        id: entry.id,
        name: entry.courseName,
        code: entry.courseCode,
        moduleName: entry.moduleName,
        studyName: entry.studyName,
        yearLabel: entry.yearLabel,
        studyId: entry.studyId,
        yearId: entry.yearId
      });
    });

    return Array.from(deduped.values())
      .sort((left, right) => {
        const studyCompare = left.studyName.localeCompare(right.studyName, 'nl');
        if (studyCompare !== 0) return studyCompare;
        const yearCompare = String(left.yearLabel).localeCompare(String(right.yearLabel), 'nl');
        if (yearCompare !== 0) return yearCompare;
        return left.name.localeCompare(right.name, 'nl');
      });
  }, [courseMetadata, getCourseById]);

  const assignmentRows = useMemo(() => {
    return data.enrollments.map((enrollment) => {
      const course = getCourseById(enrollment.courseId);
      const student = data.students.find((item) => String(item.id) === String(enrollment.studentId));
      const metadata = courseMetadataById.get(String(enrollment.courseId)) || {};
      const courseYearId = getCourseStudyYearId(course);
      let linkedYear = studyYearLookup.get(courseYearId);
      if (!linkedYear) {
        const mids = [
          toId(course?.module?.id || course?.module?._id || course?.module),
          ...(course?.modules || []).map((m) => toId(m?.id || m?._id || m))
        ].filter(Boolean);
        for (const mid of mids) {
          const e = moduleToYearLookup.get(mid);
          if (e) { linkedYear = e; break; }
        }
      }
      const fallbackStudyId = String(enrollment.studyId || linkedYear?.study?.id || linkedYear?.study?._id || '');
      const fallbackStudy = studies.find((item) => String(item.id || item._id) === fallbackStudyId);
      const courseModuleNames = (course?.modules || [])
        .map((moduleDoc) => moduleDoc?.name || moduleDoc?.code)
        .filter(Boolean);

      return {
        ...enrollment,
        course,
        student,
        studyId: metadata.studyId || fallbackStudyId,
        studyName: metadata.studyName || fallbackStudy?.name || linkedYear?.study?.name || 'Onbekende studie',
        yearId: metadata.yearId || courseYearId || String(linkedYear?.year?.id || linkedYear?.year?._id || ''),
        yearLabel: metadata.yearLabel || linkedYear?.year?.name || `Studiejaar ${Number(enrollment.currentStudyYear || linkedYear?.year?.yearNumber || 1)}`,
        yearNumber: metadata.yearNumber || linkedYear?.year?.yearNumber || linkedYear?.year?.year || null,
        currentStudyYear: Number(enrollment.currentStudyYear || 1),
        studyYearMode: enrollment.studyYearMode || 'auto',
        moduleId: metadata.moduleId || '',
        moduleName: courseModuleNames.length > 0 ? courseModuleNames.join(', ') : (metadata.moduleName || 'Geen module gekoppeld'),
        courseLocation: course?.location || metadata.courseLocation || '',
        studentLocation: student?.location || ''
      };
    });
  }, [courseMetadataById, data.enrollments, data.students, getCourseById, getCourseStudyYearId, studies, studyYearLookup, moduleToYearLookup]);

  const isWithinPeriod = (value) => {
    if (periodFilter === 'all' && !selectedMonth) return true;
    if (!value) return false;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;

    if (selectedMonth) {
      const [year, month] = selectedMonth.split('-').map(Number);
      if (date.getFullYear() !== year || date.getMonth() !== month - 1) {
        return false;
      }
    }

    if (periodFilter === 'all') return true;

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (periodFilter === 'week') {
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfToday.getDate() - 6);
      return date >= startOfWeek;
    }

    if (periodFilter === 'month') {
      return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
    }

    if (periodFilter === 'year') {
      return date.getFullYear() === now.getFullYear();
    }

    return true;
  };

  const matchesSearch = (enrollment) => {
    const haystack = [
      enrollment.student?.name,
      enrollment.student?.email,
      enrollment.studyName,
      enrollment.yearLabel,
      enrollment.moduleName,
      enrollment.course?.name,
      enrollment.course?.code
    ]
      .filter(Boolean)
      .join(' ');

    return includesNormalized(haystack, searchTerm);
  };

  const matchesSelectedLocation = (enrollment) => {
    if (!selectedLocation || selectedLocation === 'all') {
      return true;
    }

    const normalizedLocation = String(selectedLocation).toLowerCase();
    return String(enrollment.courseLocation || enrollment.studentLocation || '').toLowerCase() === normalizedLocation;
  };

  const studentsForEnrollModal = useMemo(() => {
    if (!enrollModalStudy) return data.students;
    const cohortIds = new Set(
      (data.cohorts || []).filter((c) =>
        (c.studies || []).some((s) => String(s.id || s._id || s) === enrollModalStudy)
      ).map((c) => String(c.id || c._id))
    );
    return data.students.filter((s) => {
      const cohortId = String(s.cohort?.id || s.cohort?._id || s.cohort || '');
      if (!cohortId) {
        // Keep students without cohort visible for individual assignment.
        return true;
      }
      return cohortIds.has(cohortId);
    });
  }, [data.students, data.cohorts, enrollModalStudy]);

  const filteredStudentsForEnrollModal = useMemo(() => {
    const query = enrollStudentSearch.trim();
    if (!query) return studentsForEnrollModal;
    return studentsForEnrollModal.filter((student) => {
      const haystack = `${student.name || ''} ${student.email || ''}`;
      return includesNormalized(haystack, query);
    });
  }, [studentsForEnrollModal, enrollStudentSearch]);

  const coursesForEnrollModal = useMemo(() => {
    return allCourses.filter((c) => {
      const meta = courseMetadataById.get(String(c.id));
      if (!meta) return !enrollModalStudy && !enrollModalYear;
      if (enrollModalStudy && meta.studyId !== enrollModalStudy) return false;
      if (enrollModalYear && meta.yearId !== enrollModalYear) return false;
      return true;
    });
  }, [allCourses, courseMetadataById, enrollModalStudy, enrollModalYear]);

  const yearsForEnrollModal = useMemo(() => {
    const candidate = enrollModalStudy
      ? courseMetadata.filter((entry) => String(entry.studyId) === String(enrollModalStudy))
      : courseMetadata;

    return Array.from(new Map(candidate.map((entry) => [entry.yearId, entry])).values())
      .sort((left, right) => Number(left.yearNumber || 0) - Number(right.yearNumber || 0));
  }, [courseMetadata, enrollModalStudy]);

  const cohortsForBulkEnroll = useMemo(() => {
    return [...(data.cohorts || [])]
      .filter((cohort) => (cohort.studentIds || []).length > 0)
      .sort((left, right) => String(left.name || '').localeCompare(String(right.name || ''), 'nl'));
  }, [data.cohorts]);

  const selectedBulkCohort = useMemo(() => {
    return (data.cohorts || []).find((cohort) => String(cohort.id || cohort._id) === String(bulkCohort));
  }, [data.cohorts, bulkCohort]);

  const selectedBulkCohortStudyIds = useMemo(() => {
    return new Set((selectedBulkCohort?.studies || []).map((study) => String(study.id || study._id || study)));
  }, [selectedBulkCohort]);

  const coursesForBulkEnroll = useMemo(() => {
    return allCourses.filter((course) => {
      if (selectedBulkCohortStudyIds.size > 0 && !selectedBulkCohortStudyIds.has(String(course.studyId))) {
        return false;
      }
      if (bulkYear && String(course.yearId) !== String(bulkYear)) {
        return false;
      }
      return true;
    });
  }, [allCourses, bulkYear, selectedBulkCohortStudyIds]);

  const yearsForBulkEnroll = useMemo(() => {
    const candidate = selectedBulkCohortStudyIds.size > 0
      ? courseMetadata.filter((entry) => selectedBulkCohortStudyIds.has(String(entry.studyId)))
      : courseMetadata;

    return Array.from(new Map(candidate.map((entry) => [entry.yearId, entry])).values())
      .sort((left, right) => Number(left.yearNumber || 0) - Number(right.yearNumber || 0));
  }, [courseMetadata, selectedBulkCohortStudyIds]);

  const studentsForBulkEnroll = useMemo(() => {
    const selectedCohort = (data.cohorts || []).find((cohort) => String(cohort.id) === String(bulkCohort));
    if (!selectedCohort) return [];

    const cohortStudentIds = new Set((selectedCohort.studentIds || []).map((id) => String(id)));
    const query = bulkStudentSearch.trim();

    return (data.students || []).filter((student) => {
      if (!cohortStudentIds.has(String(student.id))) {
        return false;
      }
      if (!query) {
        return true;
      }
      return includesNormalized(`${student.name || ''} ${student.email || ''}`, query);
    });
  }, [data.cohorts, data.students, bulkCohort, bulkStudentSearch]);

  const alreadyEnrolledStudentIdsForBulkCourse = useMemo(() => {
    if (!bulkCourse) return new Set();
    return new Set(
      (data.enrollments || [])
        .filter((enrollment) => String(enrollment.courseId) === String(bulkCourse))
        .map((enrollment) => String(enrollment.studentId))
    );
  }, [data.enrollments, bulkCourse]);

  const eligibleStudentsForBulkEnroll = useMemo(() => {
    if (!bulkCourse) return studentsForBulkEnroll;
    return studentsForBulkEnroll.filter((student) => !alreadyEnrolledStudentIdsForBulkCourse.has(String(student.id)));
  }, [alreadyEnrolledStudentIdsForBulkCourse, bulkCourse, studentsForBulkEnroll]);

  const skippedStudentsPreview = useMemo(() => {
    if (!bulkCourse) return [];
    return studentsForBulkEnroll
      .filter((student) => alreadyEnrolledStudentIdsForBulkCourse.has(String(student.id)))
      .map((student) => ({
        id: student.id,
        name: student.name,
        email: student.email,
        reason: 'Al ingeschreven voor dit vak'
      }));
  }, [alreadyEnrolledStudentIdsForBulkCourse, bulkCourse, studentsForBulkEnroll]);

  const formatBulkError = (errorMessage, reasonCode) => {
    const code = String(reasonCode || '').trim().toLowerCase();
    if (code === 'already_enrolled_course') return 'Al actief ingeschreven voor dit vak';
    if (code === 'already_completed_course') return 'Vak is al afgerond voor deze student';
    if (code === 'course_full') return 'Vak heeft geen vrije plaatsen';
    if (code === 'study_not_resolved') return 'Vak is niet gekoppeld aan een studie';
    if (code === 'student_not_found') return 'Student niet gevonden of geen student-account';

    const lower = String(errorMessage || '').toLowerCase();
    if (lower.includes('al ingeschreven')) return 'Al actief ingeschreven voor dit vak';
    if (lower.includes('afgerond')) return 'Vak is al afgerond voor deze student';
    if (lower.includes('cursus is vol')) return 'Vak heeft geen vrije plaatsen';
    if (lower.includes('studie kon niet worden bepaald')) return 'Vak is niet gekoppeld aan een studie';
    if (lower.includes('niet gevonden')) return 'Student niet gevonden of ongeldig';
    return errorMessage || 'Onbekende reden';
  };

  const escapeCsvCell = (value) => {
    const text = String(value ?? '');
    const escaped = text.replace(/"/g, '""');
    return `"${escaped}"`;
  };

  const downloadBulkSkippedCsv = () => {
    if (!bulkResult?.skipped?.length) return;

    const selectedCourseDoc = allCourses.find((course) => String(course.id) === String(bulkCourse));
    const selectedCohortDoc = (data.cohorts || []).find((cohort) => String(cohort.id || cohort._id) === String(bulkCohort));
    const selectedYearDoc = yearsForBulkEnroll.find((year) => String(year.yearId) === String(bulkYear));

    const header = ['studentId', 'studentNaam', 'reden', 'cohort', 'studiejaar', 'vakCode', 'vakNaam'];
    const rows = bulkResult.skipped.map((entry) => [
      entry.studentId,
      entry.studentName,
      entry.reason,
      selectedCohortDoc?.name || '',
      selectedYearDoc?.yearLabel || '',
      selectedCourseDoc?.code || '',
      selectedCourseDoc?.name || ''
    ]);

    const csvContent = [
      header.map(escapeCsvCell).join(','),
      ...rows.map((row) => row.map(escapeCsvCell).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateStamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `bulk-overgeslagen-${dateStamp}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleEnroll = (e) => {
    e.preventDefault();
    if (selectedCourse && selectedStudent) {
      enrollStudent(selectedStudent, selectedCourse, notificationSettings);
      setSelectedCourse('');
      setSelectedStudent('');
      setEnrollModalStudy('');
      setEnrollModalYear('');
      setEnrollStudentSearch('');
      setShowEnrollModal(false);
      setNotificationSettings({ sendEmail: true, sendSms: false });
    }
  };

  const handleBulkEnroll = async (e) => {
    e.preventDefault();
    if (!bulkCourse || eligibleStudentsForBulkEnroll.length === 0) return;

    const studentLookup = new Map((data.students || []).map((student) => [String(student.id), student]));

    try {
      const result = await bulkEnrollStudents(
        eligibleStudentsForBulkEnroll.map((student) => student.id),
        bulkCourse,
        notificationSettings
      );

      const backendErrors = (result?.errors || []).map((entry) => {
        const student = studentLookup.get(String(entry.studentId));
        return {
          studentId: entry.studentId,
          studentName: student?.name || `Student ${entry.studentId}`,
          reason: formatBulkError(entry.error, entry.reasonCode),
          rawError: entry.error
        };
      });

      const localSkipped = skippedStudentsPreview.map((student) => ({
        studentId: student.id,
        studentName: student.name,
        reason: student.reason,
        rawError: 'Pre-check: already enrolled'
      }));

      const combinedSkipped = [...localSkipped, ...backendErrors];
      setBulkResult({
        enrolled: Number(result?.enrolled || 0),
        skipped: combinedSkipped,
        requested: eligibleStudentsForBulkEnroll.length + localSkipped.length
      });

      if (backendErrors.length === 0 && localSkipped.length === 0) {
        setShowBulkEnrollModal(false);
        setBulkYear('');
        setBulkCohort('');
        setBulkCourse('');
        setBulkStudentSearch('');
        setBulkResult(null);
      }
    } catch (error) {
      window.alert(error.message || 'Bulk toewijzing mislukt');
    }
  };

  const handleUnenroll = (enrollmentId) => {
    if (window.confirm('Weet u zeker dat u deze student wilt uitschrijven?')) {
      unenrollStudent(enrollmentId);
    }
  };

  const handleQuickStatusToggle = async (enrollment) => {
    if (!enrollment?.id) return;
    const nextStatus = enrollment.status === 'active' ? 'cancelled' : 'active';
    await updateEnrollment(enrollment.id, {
      status: nextStatus,
      studyYearMode: enrollment.studyYearMode || 'auto',
      currentStudyYear: Number(enrollment.currentStudyYear || 1)
    });
  };

  const openEditStatus = (enrollment) => {
    setEditingEnrollment(enrollment);
    setEditingStatus(enrollment.status || 'active');
    setEditingStudyYearMode(enrollment.studyYearMode || 'auto');
    setEditingCurrentStudyYear(String(enrollment.currentStudyYear || 1));
  };

  const editableStudyYearOptions = useMemo(() => {
    if (!editingEnrollment?.studyId) return [];
    const study = studies.find((item) => String(item.id || item._id) === String(editingEnrollment.studyId));
    if (!study) return [];

    const yearNumbers = (study.studyYears || [])
      .map((year) => Number(year.yearNumber || year.year || 0))
      .filter((yearNumber) => Number.isFinite(yearNumber) && yearNumber > 0);

    return Array.from(new Set(yearNumbers)).sort((a, b) => a - b);
  }, [editingEnrollment?.studyId, studies]);

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    if (!editingEnrollment) return;

    const payload = {
      status: editingStatus,
      studyYearMode: editingStudyYearMode
    };

    if (editingStudyYearMode === 'manual') {
      payload.currentStudyYear = Number(editingCurrentStudyYear || 1);
    } else {
      payload.recalculateStudyYear = true;
    }

    await updateEnrollment(editingEnrollment.id, payload);
    setEditingEnrollment(null);
  };

  const enrollments = assignmentRows.filter((enrollment) => {
    if (!matchesSelectedLocation(enrollment)) return false;
    if (!matchesSearch(enrollment)) return false;
    if (!isWithinPeriod(enrollment.enrollmentDate)) return false;
    if (selectedStudy && enrollment.studyId !== selectedStudy) return false;
    if (selectedYear && enrollment.yearId !== selectedYear) return false;
    if (selectedAssignmentStatus && enrollment.status !== selectedAssignmentStatus) return false;
    if (selectedCourseFilter && String(enrollment.courseId) !== String(selectedCourseFilter)) return false;
    return true;
  });

  const groupedEnrollments = enrollments.reduce((accumulator, enrollment) => {
    const studyKey = enrollment.studyId || 'unknown-study';
    if (!accumulator[studyKey]) {
      accumulator[studyKey] = {
        studyId: studyKey,
        studyName: enrollment.studyName,
        years: {}
      };
    }

    const yearKey = enrollment.yearId || 'unknown-year';
    if (!accumulator[studyKey].years[yearKey]) {
      accumulator[studyKey].years[yearKey] = {
        yearId: yearKey,
        yearLabel: enrollment.yearLabel,
        yearNumber: enrollment.yearNumber,
        rows: []
      };
    }

    accumulator[studyKey].years[yearKey].rows.push(enrollment);
    return accumulator;
  }, {});

  const groupedSections = Object.values(groupedEnrollments)
    .sort((left, right) => left.studyName.localeCompare(right.studyName, 'nl'))
    .map((studyGroup) => ({
      ...studyGroup,
      years: Object.values(studyGroup.years).sort((left, right) => {
        const leftYear = Number(left.yearNumber || Number.MAX_SAFE_INTEGER);
        const rightYear = Number(right.yearNumber || Number.MAX_SAFE_INTEGER);
        if (leftYear !== rightYear) {
          return leftYear - rightYear;
        }
        return left.yearLabel.localeCompare(right.yearLabel, 'nl');
      })
    }));

  const availableYears = selectedStudy
    ? courseMetadata.filter((entry) => entry.studyId === selectedStudy)
    : courseMetadata;

  const availableCourses = allCourses.filter((entry) => {
    const metadata = courseMetadataById.get(String(entry.id));
    if (!metadata) return true;
    if (selectedStudy && metadata.studyId !== selectedStudy) return false;
    if (selectedYear && metadata.yearId !== selectedYear) return false;
    return true;
  });

  return (
    <div className="enrollments-page">
      <div className="page-header">
        <div>
          <h1>{t('admin.courseAssignments')}</h1>
          <p className="page-subtitle">{t('admin.assignmentGroupsHint')}</p>
        </div>
        <div className="header-action-row">
          <button type="button" className="btn-secondary" onClick={() => setShowBulkEnrollModal(true)}>
            Cohort aan vak koppelen
          </button>
          <button type="button" className="btn-primary" onClick={() => setShowEnrollModal(true)}>
            {t('admin.enrollStudent')}
          </button>
          <button type="button" className="btn-secondary" onClick={() => setBulkRefreshTrigger(prev => prev + 1)} title="Vernieuw gegevens van server" style={{marginLeft: 'auto'}}>
            ↻ Gegevens vernieuwen
          </button>
        </div>
      </div>

      <div className="enrollment-filter-toolbar">
        <input
          type="search"
          className="enrollment-search-input"
          placeholder="Zoek op naam, e-mail of vak"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select className="enrollment-select-input" value={selectedStudy} onChange={(e) => {
          setSelectedStudy(e.target.value);
          setSelectedYear('');
          setSelectedCourseFilter('');
        }}>
          <option value="">Alle studies</option>
          {studiesWithAssignments.map((study) => {
            const studyId = toId(study.id || study._id);
            return <option key={studyId} value={studyId}>{getStudyOptionLabel(study)}</option>;
          })}
        </select>
        <select className="enrollment-select-input" value={selectedYear} onChange={(e) => {
          setSelectedYear(e.target.value);
          setSelectedCourseFilter('');
        }}>
          <option value="">Alle studiejaren</option>
          {Array.from(new Map(availableYears.map((entry) => [entry.yearId, entry]))).values().map((entry) => (
            <option key={entry.yearId} value={entry.yearId}>{entry.yearLabel}</option>
          ))}
        </select>
        <select className="enrollment-select-input" value={selectedAssignmentStatus} onChange={(e) => setSelectedAssignmentStatus(e.target.value)}>
          <option value="">Alle statussen</option>
          <option value="active">Actief</option>
          <option value="completed">Afgerond</option>
          <option value="cancelled">Niet toegestaan / Geannuleerd</option>
        </select>
        <select className="enrollment-select-input" value={selectedCourseFilter} onChange={(e) => {
          setSelectedCourseFilter(e.target.value);
        }}>
          <option value="">Alle vakken</option>
          {availableCourses.map((course) => (
            <option key={course.id} value={course.id}>{course.code} - {course.name}</option>
          ))}
        </select>
        <input
          type="month"
          className="enrollment-month-input"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        />
      </div>

      <div className="filter-button-group enrollment-period-buttons">
        {periodOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`filter-pill ${periodFilter === option.value ? 'active' : ''}`}
            onClick={() => setPeriodFilter(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <Modal isOpen={showEnrollModal} onClose={() => { setShowEnrollModal(false); setEnrollModalStudy(''); setEnrollModalYear(''); setSelectedStudent(''); setSelectedCourse(''); setEnrollStudentSearch(''); }} title={t('admin.enrollStudent')}>
        <form onSubmit={handleEnroll} className="enroll-form single-enroll-form">
          <div className="enroll-field-section">
            <label className="enroll-field-label">Studie</label>
            <select
              value={enrollModalStudy}
              onChange={(e) => { setEnrollModalStudy(e.target.value); setEnrollModalYear(''); setSelectedStudent(''); setSelectedCourse(''); setEnrollStudentSearch(''); }}
              className="form-select"
            >
              <option value="">-- Kies studie --</option>
              {studiesWithAssignments.map((study) => {
                const studyId = toId(study.id || study._id);
                return <option key={studyId} value={studyId}>{getStudyOptionLabel(study)}</option>;
              })}
            </select>
          </div>

          <div className="enroll-field-section">
            <label className="enroll-field-label">Studiejaar</label>
            <select
              value={enrollModalYear}
              onChange={(e) => { setEnrollModalYear(e.target.value); setSelectedCourse(''); }}
              className="form-select"
              disabled={!enrollModalStudy}
            >
              <option value="">-- Kies studiejaar --</option>
              {yearsForEnrollModal.map((yearEntry) => (
                <option key={yearEntry.yearId} value={yearEntry.yearId}>{yearEntry.yearLabel}</option>
              ))}
            </select>
          </div>

          <div className="enroll-field-section">
            <label className="enroll-field-label">Vak</label>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="form-select"
              disabled={!enrollModalYear}
            >
              <option value="">-- Kies vak --</option>
              {coursesForEnrollModal.map(course => (
                <option key={course.id} value={course.id}>
                  {course.code} - {course.name}
                </option>
              ))}
            </select>
          </div>

          <div className="enroll-field-section">
            <label className="enroll-field-label">Zoek student</label>
            <input
              type="search"
              value={enrollStudentSearch}
              onChange={(e) => setEnrollStudentSearch(e.target.value)}
              placeholder="Typ studentnaam of e-mail..."
              className="form-select"
              disabled={!enrollModalStudy}
            />
          </div>

          <div className="enroll-field-section">
            <label className="enroll-field-label">Student</label>
            <select
              value={selectedStudent}
              onChange={(e) => setSelectedStudent(e.target.value)}
              className="form-select"
              disabled={!enrollModalStudy || filteredStudentsForEnrollModal.length === 0}
            >
              <option value="">-- Kies student --</option>
              {filteredStudentsForEnrollModal.map(student => (
                <option key={student.id} value={student.id}>
                  {student.name} ({student.email})
                </option>
              ))}
            </select>
            {enrollModalStudy && filteredStudentsForEnrollModal.length === 0 && (
              <p className="form-hint" style={{ color: '#d97706', marginTop: '4px' }}>
                Geen studenten gevonden voor deze selectie.
              </p>
            )}
          </div>

          <div className="enroll-notifications-section">
            <p className="enroll-section-title">Meldingen versturen</p>
            <div className="notification-checkbox">
              <input
                type="checkbox"
                id="notify-email"
                checked={notificationSettings.sendEmail}
                onChange={(e) => setNotificationSettings({...notificationSettings, sendEmail: e.target.checked})}
              />
              <label htmlFor="notify-email">E-mail versturen naar student</label>
            </div>
            <div className="notification-checkbox">
              <input
                type="checkbox"
                id="notify-sms"
                checked={notificationSettings.sendSms}
                onChange={(e) => setNotificationSettings({...notificationSettings, sendSms: e.target.checked})}
              />
              <label htmlFor="notify-sms">SMS versturen naar student</label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={!selectedStudent || !selectedCourse}>
              {t('enrollment.add')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => { setShowEnrollModal(false); setEnrollModalStudy(''); setEnrollModalYear(''); setSelectedStudent(''); setSelectedCourse(''); setEnrollStudentSearch(''); }}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showBulkEnrollModal} onClose={() => { setShowBulkEnrollModal(false); setBulkYear(''); setBulkCohort(''); setBulkCourse(''); setBulkStudentSearch(''); setBulkResult(null); }} title="Cohort aan vak koppelen">
        <form onSubmit={handleBulkEnroll} className="bulk-enroll-form">
          <div className="bulk-filter-grid">
            <div className="bulk-filter-group">
              <label>Cohort</label>
              <select
                value={bulkCohort}
                onChange={(e) => {
                  setBulkCohort(e.target.value);
                  setBulkYear('');
                  setBulkCourse('');
                  setBulkStudentSearch('');
                  setBulkResult(null);
                }}
                className="form-select"
              >
                <option value="">-- Kies cohort --</option>
                {cohortsForBulkEnroll.map((cohort) => (
                  <option key={cohort.id} value={cohort.id}>{cohort.name} ({cohort.studentCount || cohort.studentIds?.length || 0} studenten)</option>
                ))}
              </select>
            </div>

            <div className="bulk-filter-group">
              <label>Studiejaar</label>
              <select
                value={bulkYear}
                onChange={(e) => {
                  setBulkYear(e.target.value);
                  setBulkCourse('');
                  setBulkResult(null);
                }}
                className="form-select"
                disabled={!bulkCohort}
              >
                <option value="">-- Kies studiejaar --</option>
                {yearsForBulkEnroll.map((yearEntry) => (
                  <option key={yearEntry.yearId} value={yearEntry.yearId}>{yearEntry.yearLabel}</option>
                ))}
              </select>
            </div>

            <div className="bulk-filter-group">
              <label>Vak</label>
              <select
                value={bulkCourse}
                onChange={(e) => {
                  setBulkCourse(e.target.value);
                  setBulkResult(null);
                }}
                className="form-select"
                disabled={!bulkCohort || !bulkYear}
              >
                <option value="">-- Kies vak --</option>
                {coursesForBulkEnroll.map((course) => (
                  <option key={course.id} value={course.id}>{course.code} - {course.name} ({course.yearLabel})</option>
                ))}
              </select>
            </div>
          </div>

          <input
            type="search"
            value={bulkStudentSearch}
            onChange={(e) => setBulkStudentSearch(e.target.value)}
            placeholder="Filter studenten in cohort op naam of e-mail..."
            className="form-select"
            disabled={!bulkCohort}
          />

          <div className="bulk-enroll-preview bulk-enroll-summary-grid">
            <div>
              <small>In cohort (na zoekfilter)</small>
              <strong>{studentsForBulkEnroll.length}</strong>
            </div>
            <div>
              <small>Klaar voor koppelen</small>
              <strong>{eligibleStudentsForBulkEnroll.length}</strong>
            </div>
            <div>
              <small>Worden overgeslagen</small>
              <strong>{skippedStudentsPreview.length}</strong>
            </div>
          </div>

          {skippedStudentsPreview.length > 0 && (
            <div className="bulk-result-box warning">
              <p className="bulk-result-title">Vooraf gedetecteerd: studenten die worden overgeslagen</p>
              <ul className="bulk-result-list">
                {skippedStudentsPreview.slice(0, 8).map((student) => (
                  <li key={student.id}>{student.name} ({student.email}) - {student.reason}</li>
                ))}
              </ul>
              {skippedStudentsPreview.length > 8 && (
                <p className="bulk-result-more">+{skippedStudentsPreview.length - 8} meer</p>
              )}
            </div>
          )}

          {eligibleStudentsForBulkEnroll.length > 0 && (
            <div className="bulk-student-list">
              {eligibleStudentsForBulkEnroll.slice(0, 12).map((student) => (
                <span key={student.id} className="registration-course-chip">{student.name}</span>
              ))}
              {eligibleStudentsForBulkEnroll.length > 12 && (
                <span className="registration-course-chip muted">+{eligibleStudentsForBulkEnroll.length - 12} meer</span>
              )}
            </div>
          )}

          {bulkResult && (
            <div className="bulk-result-box">
              <p className="bulk-result-title">Resultaat</p>
              <p>{bulkResult.enrolled} gekoppeld, {bulkResult.skipped.length} overgeslagen (van {bulkResult.requested})</p>
              {bulkResult.skipped.length > 0 && (
                <>
                  <button type="button" className="btn-secondary" onClick={downloadBulkSkippedCsv}>
                    Exporteer overgeslagen studenten (CSV)
                  </button>
                  <br />
                  <br />
                  <details className="bulk-error-details">
                    <summary className="bulk-error-summary">Toon details van overgeslagen studenten ({bulkResult.skipped.length})</summary>
                    <ul className="bulk-result-list">
                      {bulkResult.skipped.map((entry, idx) => (
                        <li key={`${entry.studentId}-${idx}`} className="bulk-error-item">
                          <strong>{entry.studentName}</strong> - {entry.reason}
                          {entry.rawError && entry.rawError !== entry.reason && (
                            <div className="bulk-error-raw" title={entry.rawError}>
                              <small>Backend: {entry.rawError.substring(0, 80)}{entry.rawError.length > 80 ? '...' : ''}</small>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                </>
              )}
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={!bulkCohort || !bulkYear || !bulkCourse || eligibleStudentsForBulkEnroll.length === 0}>
              Koppel cohort aan vak
            </button>
            <button type="button" className="btn-secondary" onClick={() => { setShowBulkEnrollModal(false); setBulkYear(''); setBulkCohort(''); setBulkCourse(''); setBulkStudentSearch(''); setBulkResult(null); }}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </Modal>

      <div className="assignment-groups">
        {groupedSections.map((studyGroup) => (
          <section key={studyGroup.studyId} className="assignment-section">
            <div className="assignment-section-header">
              <h2>{studyGroup.studyName}</h2>
              <span>{studyGroup.years.reduce((count, year) => count + year.rows.length, 0)} toewijzingen</span>
            </div>

            {studyGroup.years.map((yearGroup) => (
              <div key={yearGroup.yearId} className="assignment-year-card">
                <div className="assignment-year-header">
                  <h3>{yearGroup.yearLabel}</h3>
                  <span>{yearGroup.rows.length} toewijzingen</span>
                </div>
                <div className="enrollments-list">
                  <table className="enrollments-table">
                    <thead>
                      <tr>
                        <th>{t('enrollment.student')}</th>
                        <th>Automatische modules</th>
                        <th>{t('enrollment.course')}</th>
                        <th>Huidig studiejaar</th>
                        <th>{t('enrollment.enrolledOn')}</th>
                        <th>{t('common.status')}</th>
                        <th>{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearGroup.rows.map((enrollment) => (
                        <tr key={enrollment.id}>
                          <td>
                            {enrollment.student?.name || 'Onbekend'}
                            <br />
                            <small>{enrollment.student?.email}</small>
                          </td>
                          <td>{enrollment.moduleName}</td>
                          <td>
                            {enrollment.course?.name || 'Onbekend'}
                            <br />
                            <small>{enrollment.course?.code} · {enrollment.yearLabel}</small>
                          </td>
                          <td>
                            <strong>Jaar {enrollment.currentStudyYear || 1}</strong>
                            <br />
                            <small>{enrollment.studyYearMode === 'manual' ? 'Handmatig' : 'Automatisch'}</small>
                          </td>
                          <td>{enrollment.enrolledOn || '-'}</td>
                          <td>
                            <span className={`status-badge ${enrollment.status}`}>
                              {t(`enrollment.status.${enrollment.status}`, t(`enrollment.${enrollment.status}`, enrollment.status))}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn-secondary"
                              onClick={() => handleQuickStatusToggle(enrollment)}
                            >
                              {enrollment.status === 'active' ? 'Niet toestaan' : 'Toestaan'}
                            </button>
                            <button
                              className="btn-secondary"
                              onClick={() => openEditStatus(enrollment)}
                            >
                              {t('common.edit')}
                            </button>
                            <button
                              className="btn-delete"
                              onClick={() => handleUnenroll(enrollment.id)}
                            >
                              {t('common.delete')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </section>
        ))}

        {groupedSections.length === 0 && (
          <p className="no-data">{t('common.noData')}</p>
        )}
      </div>

      <Modal isOpen={!!editingEnrollment} onClose={() => setEditingEnrollment(null)} title={t('common.edit')} size="sm">
        <form onSubmit={handleUpdateStatus}>
          <div className="form-row" style={{ marginBottom: '12px' }}>
            <label htmlFor="enrollment-status">{t('common.status')}</label>
            <select
              id="enrollment-status"
              value={editingStatus}
              onChange={(e) => setEditingStatus(e.target.value)}
              className="form-select"
            >
              <option value="active">{t('enrollment.status.active')}</option>
              <option value="completed">{t('enrollment.status.completed')}</option>
              <option value="cancelled">{t('enrollment.status.cancelled')}</option>
            </select>
          </div>

          <div className="form-row" style={{ marginBottom: '12px' }}>
            <label htmlFor="study-year-mode">Studiejaar modus</label>
            <select
              id="study-year-mode"
              value={editingStudyYearMode}
              onChange={(e) => setEditingStudyYearMode(e.target.value)}
              className="form-select"
            >
              <option value="auto">Automatisch (op basis van behaalde vakken)</option>
              <option value="manual">Handmatig instellen</option>
            </select>
          </div>

          {editingStudyYearMode === 'manual' && (
            <div className="form-row" style={{ marginBottom: '12px' }}>
              <label htmlFor="current-study-year">Huidig studiejaar</label>
              <select
                id="current-study-year"
                value={editingCurrentStudyYear}
                onChange={(e) => setEditingCurrentStudyYear(e.target.value)}
                className="form-select"
              >
                {(editableStudyYearOptions.length > 0 ? editableStudyYearOptions : [1, 2, 3, 4]).map((yearNumber) => (
                  <option key={yearNumber} value={String(yearNumber)}>Jaar {yearNumber}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn-primary">{t('common.save')}</button>
            <button type="button" className="btn-secondary" onClick={() => setEditingEnrollment(null)}>{t('common.cancel')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Enrollments;
